// frontend/src/pages/PurchaseRecommendations.jsx

import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useTranslation } from 'react-i18next';
import { useTabs } from '../tabs/useTabs';
import { useTabActive } from '../tabs/tabsContexts';
import { usePurchaseOrderCreated } from '../hooks/usePurchaseOrderCreated';
import "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import {
  Button,
  Box,
  CircularProgress,
  Typography,
  Tooltip,
} from "@mui/material";
import { ICON, ArrowsClockwise, Plus, Warning } from '../icons';
import StatusChip from '../components/common/StatusChip';
import PageBanner from '../components/common/PageBanner';
import ChipSlot from '../components/grid/ChipSlot';
import LinkSupplierToExactDialog from '../components/settings/LinkSupplierToExactDialog';
import { useActiveErp } from '../hooks/useConnectors';
import { useWriteGate } from '../hooks/useWriteGate';
import Tile from '../components/layout/Tile';
import { ItemFilterBar, useItemFilter } from '../features/item-filter';
import { useTabPersistedState } from '../tabs/useTabPersistedState';
import { useSsrmGrid, buildExportQuery } from '../hooks/useSsrmGrid';
import { useChangeEffect } from '../hooks/useChangeEffect';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiJson } from '../queries/api';
import { usePOPlanningSummary } from '../hooks/usePOData';
import { useRunPORecommendationsJob } from '../hooks/usePOMutations';
import { useSetDataHealthIgnored } from '../hooks/useDataHealthMutations';
import { sessionApi, sessionService } from '../services/SessionApiService';
import { poRecommendationLines as poRecommendationLinesKey } from '../queries/keys';
import { DATA, gridReads, useDataChanged, useOnDataChanged } from '../queries/dataChanged';
import { translateRequestError } from '../queries/api';
import { useAgGridTheme } from '../utils/agGridTheme';
import { defaultColDef, defaultGridProps, editableGridProps, copyOnlyContextMenuItems, actionColumn, flexColumn, everyRowIsMaster } from '../utils/gridDefaults';
import { useAuth } from '../contexts/AuthContext';
import { useFormatters } from '../hooks/useFormatters';
import { useStoreToday } from '../hooks/useStoreToday';
import {
  runsOutSeverity,
  RUNS_OUT_NONE, RUNS_OUT_ALREADY, RUNS_OUT_TODAY, RUNS_OUT_SOON,
} from '../utils/runsOutSeverity';
import { useColumnTypes } from '../hooks/useColumnTypes';

// A line's `shortage` on the wire (PORecommendationLine.shortage).
const SHORTAGES = [null, 'out_of_stock', 'will_run_out'];
import { useGridState } from '../hooks/useGridState';
import { useConfirmDialog } from '../hooks/useConfirmDialog';
import { buildPinnedTotalsRow, wrapColumnsForSummaryRows, isPinnedTotalRow } from '../utils/pinnedTotals';
import ConfirmDialog from '../components/common/ConfirmDialog';
import GridExportButton from '../components/common/GridExportButton';
import FilterChips from '../components/common/FilterChips';
import DetailGrid from '../components/grid/DetailGrid';
import DetailRow from '../components/grid/DetailRow';
import ExpandAllButton from '../components/grid/ExpandAllButton';
import { useExpandAllRows } from '../hooks/useExpandAllRows';
import { useIsShopify, itemIdentityColumns } from '../components/grid/productIdentity';
import PurchaseOrderForm from '../components/purchase-orders/PurchaseOrderForm';
import { useReorderLevels } from '../components/reorderLevels/useReorderLevels';
import RecommendationRowActions from '../components/purchase-orders/RecommendationRowActions';
import { totalValueWarning } from './recommendationTotalWarning';
import { lineValue, tickedTotals } from './recommendationTickedTotals';
import '../styles/pages.css';
import { GridEmptyState } from '../components/grid/NoRowsOverlay';
import { showToast } from '../components/common/appToastBus';
import { GridPage, GridViewport } from '../components/grid/GridPage';

// Newest calculation job for the store — polled during a run, and re-checked
// on focus regain as the staleness marker (its completed id changing means
// every recommendation id on screen is dead).
const LATEST_JOB_URL = '/api/po-calculations/jobs/?page=1&page_size=1';
const RECOMMENDATIONS_URL = '/api/po-calculations/recommendations/';

// The lines the merchant includes in the purchase order are the line grid's
// selected rows. A collapse destroys that grid and Create PO reads the choice
// later, so the page keeps the EXCLUDED lines (context): each load ticks every
// line not excluded. The merchant's own clicks and keys write back, never the
// grid's own selection events (new rows arrive unticked, `rowDataChanged`; the
// load's own ticks are `api`).
const LINE_SELECTION = { mode: 'multiRow', checkboxes: true, headerCheckbox: true };
const MERCHANT_SELECTION_SOURCES = new Set([
  'checkboxSelected', 'spaceKey', 'keyboardSelectAll', 'uiSelectAll',
]);
const getLineRowId = (params) => String(params.data.PORecommendationLineID);

// One recommendation's lines: the detail grid reads them, and the page reads
// them again for a recommendation with unticked lines (its re-read below).
const recommendationLinesQuery = (storeId, recId) => ({
  queryKey: poRecommendationLinesKey.byRec(storeId, recId),
  queryFn: () => apiJson
    .get(`/api/po-calculations/recommendations/${recId}/`)
    .then((d) => d.lines),
  staleTime: 0,
});

// The ids of a recommendation's unticked lines, from the page's
// Map<recId, Map<lineId, line>>.
const untickedLineIds = (excludedLines, recId) => new Set(excludedLines.get(recId)?.keys());

// Recommendation-lines sub-grid, rendered through DetailGrid as a custom
// detailCellRenderer. Lines are fetched lazily on expand; the master row's supplier
// currency arrives via `data` and the include/exclude wiring via `context`
// (LINE_SELECTION above).
// staleTime 0: every calculation run (manual, nightly DAG, another session)
// DELETES all pending recommendations and recreates them with NEW ids
// (services.py _replace_pending_recommendations) — an id is valid only within
// one run, so a cached snapshot must never be served on a later expand, and a
// 404 here means the run was superseded (handled below as a staleness signal,
// not a fetch error).
// Named export for PurchaseRecommendations.detailLines.test.jsx, which pins
// the 404→superseded behaviour and the re-read after a PO against a real query
// cache; the page itself is the only production consumer.
export function RecoDetail({ data, context }) {
  const { t } = useTranslation();
  const isShopify = useIsShopify();
  const { formatNumber, formatCurrency } = useFormatters();
  const { selectedStore } = useAuth();
  const recId = data.PORecommendationID;
  const supplierCurrency = data.supplier_currency_code;

  // `lines` stays undefined until the fetch answers: the grid shows its
  // loading overlay, and its first rowDataUpdated carries the real lines.
  const { data: lines, isError, error } = useQuery({
    ...recommendationLinesQuery(selectedStore?.id, recId),
    enabled: !!selectedStore?.id && !!recId,
  });

  const tickIncludedLines = useCallback(({ api }) => {
    const excluded = context.excludedLineIds(recId);
    const included = [];
    api.forEachNode((node) => {
      if (!excluded.has(node.data.PORecommendationLineID)) included.push(node);
    });
    api.setNodesSelected({ nodes: included, newValue: true, source: 'api' });
    // Fresh lines re-record the unticked ones, so the row's totals take off
    // their quantity and cost as the server now reads them.
    if (excluded.size > 0) context.setLineSelection(recId, api);
  }, [context, recId]);
  const onLineSelectionChanged = useCallback(({ api, source }) => {
    if (MERCHANT_SELECTION_SOURCES.has(source)) context.setLineSelection(recId, api);
  }, [context, recId]);

  // 404 = this recommendation's run was replaced since the rows were fetched
  // (ids are per-run). A staleness signal for the page — which purges the
  // grid and toasts once — never a fetch error.
  const superseded = error?.status === 404;
  useEffect(() => {
    if (superseded) context?.onRecommendationsSuperseded?.();
  }, [superseded, context]);

  // "Runs out in" — days from the store's today until this item's stock hits
  // zero if nothing new is ordered (backend days_until_stockout, derived per
  // request from PORecommendationLine.ProjectedStockoutDate; the same number
  // on every line of the item), read together with the line's
  // is_out_of_stock flag so an item that is already empty says so. The
  // urgency ladder lives in runsOutSeverity. With no stockout projected, the
  // line says how far the projection checked instead (days_projected, from
  // PORecommendationLine.ProjectedThrough).
  //
  // The same cell marks the line behind the master row's Status chip (the
  // line's shortage, PORecommendationLine.shortage). An out-of-stock line
  // already reads "Out of stock" here; a will-run-out line gets its count
  // drawn as the same red chip, with the fact on hover, so an order with one
  // such line and four fine ones shows which one. The count itself is never
  // changed. A will-run-out line with no projected stockout carries the
  // chip's own words.
  //
  // Every cell reserves the fixed-label chips (ChipSlot), so a screen of
  // day counts cannot leave an Out of stock row below it clipped; a day
  // count's own width varies only with its digits.
  const RunsOutCellRenderer = useCallback((props) => {
    const n = props.value;
    const { is_out_of_stock: outOfStock, shortage } = props.data;
    if (!SHORTAGES.includes(shortage)) {
      throw new TypeError(`Runs out in: unknown shortage ${shortage}`);
    }
    const alreadyLabel = t('purchaseRecommendations.detail.runsOutAlready');
    const todayLabel = t('purchaseRecommendations.detail.runsOutToday');
    const already = <StatusChip label={alreadyLabel} color="error" />;
    const today = <StatusChip label={todayLabel} color="error" />;
    const willRunOut = <StatusChip label={t('purchaseRecommendations.columns.statusWillRunOut')} color="error" />;
    const severity = runsOutSeverity(n, outOfStock);
    // Null when no stockout is projected.
    const label = (() => {
      switch (severity) {
        case RUNS_OUT_NONE: return null;
        case RUNS_OUT_ALREADY: return alreadyLabel;
        case RUNS_OUT_TODAY: return todayLabel;
        default: return t('purchaseRecommendations.detail.runsOutDays', { count: n });
      }
    })();
    const shown = (() => {
      if (shortage === 'will_run_out') {
        const chip = label === null ? willRunOut : <StatusChip label={label} color="error" />;
        return <Tooltip title={t('purchaseRecommendations.detail.willRunOutTooltip')}>{chip}</Tooltip>;
      }
      switch (severity) {
        case RUNS_OUT_NONE: {
          const checked = props.data.days_projected;
          if (!Number.isInteger(checked)) {
            throw new TypeError(`Runs out in: no stockout and no days_projected (${checked})`);
          }
          // The last day the projection checked is behind the store's today:
          // the plan says nothing about now.
          if (checked < 0) return <span>—</span>;
          return <span>{t('purchaseRecommendations.detail.runsOutMoreThan', { count: checked })}</span>;
        }
        case RUNS_OUT_ALREADY:
          return already;
        case RUNS_OUT_TODAY:
          return today;
        case RUNS_OUT_SOON:
          return <StatusChip label={label} color="warning" />;
        default:
          return <span>{label}</span>;
      }
    })();
    return <ChipSlot shown={shown} alternatives={[already, today, willRunOut]} />;
  }, [t]);

  const columnDefs = useMemo(() => [
    // Shopify: one merchant-facing Product cell (image + name + SKU) replaces
    // the internal item_code / SKU / name trio. Non-Shopify keeps the explicit
    // identifier columns since item_code is a real, merchant-known value there.
    ...itemIdentityColumns(isShopify, {
      productHeader: t('purchaseRecommendations.detail.product'),
      codeHeader: t('purchaseRecommendations.detail.itemCode'),
      skuHeader: t('purchaseRecommendations.detail.sku'),
      nameHeader: t('purchaseRecommendations.detail.item'),
    }),
    {
      field: "Available",
      headerName: t('purchaseRecommendations.detail.available'),
      type: 'numberColumn',
    },
    {
      // Real supply only: open PO lines + committed inbound transfers within
      // this order's coverage window. The engine's own earlier proposals from
      // the same run are the separate Planned inbound column — folding them
      // in here would read as goods on the water that aren't.
      field: "IncomingStock",
      headerName: t('purchaseRecommendations.detail.inTransit'),
      type: 'numberColumn',
    },
    {
      field: "PlannedInbound",
      headerName: t('purchaseRecommendations.detail.plannedInbound'),
      headerTooltip: t('purchaseRecommendations.detail.plannedInboundTooltip'),
      type: 'numberColumn',
    },
    {
      // The demand term of THIS row's own quantity: the forecast summed
      // from today to the day this cycle's order lands. It is what makes
      // the row add up on screen — Demand until delivery + Target stock −
      // Available − (In transit + Planned inbound) = Order qty — and it
      // differs down the group because each cycle lands on a different day.
      field: "ExpectedDemand",
      headerName: t('purchaseRecommendations.detail.expectedDemand'),
      headerTooltip: t('purchaseRecommendations.detail.expectedDemandTooltip'),
      type: 'numberColumn',
    },
    {
      field: "target_stock_units",
      headerName: t('purchaseRecommendations.detail.targetStock'),
      headerTooltip: t('common.tooltips.targetStock'),
      type: 'numberColumn',
    },
    {
      field: "days_until_stockout",
      headerName: t('purchaseRecommendations.detail.runsOutIn'),
      type: 'centeredColumn',
      cellRenderer: RunsOutCellRenderer,
    },
    {
      // The recommendation to trust — read-only; the PO form is the single
      // authoring surface.
      field: "RecommendedQuantity",
      headerName: t('purchaseRecommendations.detail.orderQty'),
      type: 'numberColumn',
      editable: false,
    },
    {
      // Header is currency-free; per-cell formatter renders in the parent
      // recommendation's supplier currency (from the master row).
      headerName: t('purchaseRecommendations.detail.lineValue'),
      type: 'numberColumn',
      // Null — an em-dash, not a zero — when the item holds no cost of record;
      // the master row's warning says how many lines that is.
      valueGetter: (params) => lineValue(params.data),
      valueFormatter: (params) => {
        if (params.value == null) return '—';
        return supplierCurrency
          ? formatCurrency(params.value, supplierCurrency)
          : formatNumber(params.value, 2);
      },
    },
    {
      field: "AdjustmentReason",
      headerName: t('purchaseRecommendations.detail.notes'),
      // AdjustmentReason is a machine-readable list of {code, params}. Each code
      // is translated; a max-order-quantity cap is surfaced as a warning chip
      // whose tooltip shows the uncapped quantity and shortfall (and is filtered
      // out of the plain-text reasons so the fact shows once).
      cellRenderer: (params) => {
        const adjustments = params.data?.AdjustmentReason || [];
        const uncapped = params.data?.UncappedQuantity;
        const texts = adjustments
          .filter((a) => a.code !== 'capped_at_max')
          .map((a) => t(`purchaseRecommendations.adjustments.${a.code}`, a.params));
        const capChip = uncapped != null ? (
          <Tooltip
            title={t('purchaseRecommendations.detail.cappedTooltip', {
              uncapped: formatNumber(uncapped),
              shortfall: formatNumber(uncapped - params.data.RecommendedQuantity),
            })}
          >
            <StatusChip color="warning" label={t('purchaseRecommendations.detail.cappedChip')} />
          </Tooltip>
        ) : null;
        if (!texts.length && !capChip) return '';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, height: '100%' }} onMouseDown={e => e.stopPropagation()}>
            {texts.length ? <span>{texts.join(' · ')}</span> : null}
            {capChip}
          </Box>
        );
      },
    },
  ], [t, isShopify, formatNumber, formatCurrency, supplierCurrency, RunsOutCellRenderer]);

  // Failed fetch must read as a failure, not as a recommendation with no lines.
  if (isError) {
    // Superseded: the master row itself is already gone server-side and the
    // purge fired above is removing it — nothing here is actionable.
    if (superseded) return null;
    return <DetailRow>{translateRequestError(error, t, 'common.fetchError')}</DetailRow>;
  }

  return (
    <DetailGrid
      rowData={lines}
      columnDefs={columnDefs}
      stateKey="purchase_recs_detail_v2"
      context={context}
      {...editableGridProps}
      getRowId={getLineRowId}
      rowSelection={LINE_SELECTION}
      onRowDataUpdated={tickIncludedLines}
      onSelectionChanged={onLineSelectionChanged}
    />
  );
}

// The grid only ever shows pending recommendations; every export keeps that.
const BASE_SCOPE = { status: 'pending' };

// `dueToday`: opened on the "Due today" view (routeRegistry's
// /purchase-recommendations/due-today, the stockout alert mail's link) rather
// than on the view the merchant chose last.
function PurchaseRecommendations({ dueToday = false }) {
  const { t } = useTranslation();
  const { openTab, setOwnTitle } = useTabs();
  const onPurchaseOrderCreated = usePurchaseOrderCreated();
  const changed = useDataChanged();
  const runJobMutation = useRunPORecommendationsJob();
  const [initialCheckDone, setInitialCheckDone] = useState(false);
  const [runningCalculation, setRunningCalculation] = useState(false);
  const recalcInFlightRef = useRef(false);
  const { confirm, dialogProps } = useConfirmDialog();
  const setIssueIgnored = useSetDataHealthIgnored();
  const gridTheme = useAgGridTheme();
  const columnTypes = useColumnTypes();
  const gridState = useGridState('purchase_recommendations');
  const { formatNumber, formatCurrency, formatDate } = useFormatters();
  const storeToday = useStoreToday();
  const { userContext, updatePreference, selectedStore } = useAuth();
  const queryClient = useQueryClient();
  const [filterActionable, setFilterActionable] = useTabPersistedState(
    'filterActionable',
    dueToday || (userContext?.user?.ui_preferences?.purchase_recs_filter_actionable ?? true),
  );
  // The tab names the view it opened on, so it is tellable apart from a plain
  // "Create purchase orders" tab. Keyed on the VIEW, not the route: once the
  // merchant presses "All", a title still saying "due today" would be a lie.
  useEffect(() => {
    if (!dueToday) return;
    setOwnTitle(filterActionable
      ? t('purchaseRecommendations.dueTodayTabTitle')
      : t('nav.createPurchaseOrders'));
  }, [dueToday, filterActionable, setOwnTitle, t]);
  const [selectedLocation, setSelectedLocation] = useTabPersistedState('selectedLocation', "all");
  const [selectedProductCategory, setSelectedProductCategory] = useTabPersistedState(
    'selectedProductCategory',
    null,
  );
  const [selectedSupplier, setSelectedSupplier] = useTabPersistedState('selectedSupplier', null);
  const itemFilter = useItemFilter();
  // Filtered-dataset totals (recommended quantity + line count) — backend
  // returns these alongside count/results in every list response.
  const [totals, setTotals] = useState(null);
  // {all, due_today}: how many recs each filter button shows under every
  // other filter (views._filter_counts). null until the first list response.
  const [filterCounts, setFilterCounts] = useState(null);

  // Everything that needs the merchant's attention on this page renders as one
  // row of a single banner below — run failed, items held out of planning,
  // ERP-blocked recs, stale recs, early emergency recs — all read from the one
  // planning-summary response, and the X is its only control. Two rows also
  // carry a PERMANENT opt-out (missing-supplier exclusions, early emergency
  // alerts); when either is on screen the X first asks once, and confirming
  // persists every opt-out present — see handleDismissAttention. Re-read on
  // every visit (usePOPlanningSummary).
  const { data: planningSummary, error: planningSummaryError } = usePOPlanningSummary();

  // ERP-link gating. While the ERP owns the PO lifecycle, a rec can only be
  // ordered if its supplier/location/items are linked (erp_id stamped) — the
  // backend stamps `erp_order_block` on each row and counts the blocked recs
  // in planning summary. We surface the block up-front (a link action or a
  // "Can't order" chip in place of "Review & create") instead of letting it
  // fail at form-save.
  const { connector: erpConnector, error: connectorsError } = useActiveErp();
  // New POs originate in the ERP while it owns the PO lifecycle.
  const canCreatePO = useWriteGate('po_final').mayWrite;
  const { open: openReorderLevels } = useReorderLevels();
  // The planning-summary counts below are undefined until it lands; each is
  // only read through `> 0`, so no row shows before then.
  const recommendationsBlocked = planningSummary?.recommendations_blocked;

  // Stale recs: a pending rec whose batched supplier no longer matches a line
  // item's current default supplier (item reassigned since the last calc). Its
  // supplier, lead time, and quantities are computed against a supplier no
  // longer in use — recalculating (below) regenerates them.
  const recommendationsStale = planningSummary?.recommendations_stale;

  // Early emergency recs: scheduled for a future order day (the engine keeps
  // dates on the supplier's cycle) but carrying an emergency line — an item
  // at/below its emergency threshold (purchase_orders.actionable). They count
  // as "due today" on the bell and in this page's Due Today view so the
  // merchant can decide to order early; this banner explains why future-dated
  // rows appear there.
  const emergencyEarly = planningSummary?.emergency_early;
  const emergencyAlertsEnabled = planningSummary?.emergency_early_alerts_enabled;

  const filterOptions = useMemo(() => [
    { value: 'dueToday', label: t('purchaseRecommendations.dueTodayFilter'), count: filterCounts?.due_today },
    { value: 'all', label: t('purchaseRecommendations.allFilter'), count: filterCounts?.all },
  ], [filterCounts, t]);
  const pressFilter = (value) => {
    const actionable = value === 'dueToday';
    setFilterActionable(actionable);
    updatePreference('purchase_recs_filter_actionable', actionable);
  };

  // The rec whose supplier the merchant is linking (null = dialog closed).
  const [linkRec, setLinkRec] = useState(null);

  // The items held out of planning RIGHT NOW, per reason in fix-first order —
  // planning-summary reads them from Data health with mutes applied, so a gap
  // the merchant closes anywhere leaves this line on the next read, and it
  // names the same items as the Data health cards behind the button (counted
  // in items, where a card may count pairs or placements). The counts can
  // overlap (an item can lack both a supplier and a count), so
  // they are listed, never summed. Nothing renders before the summary lands.
  const exclusions = planningSummary ? planningSummary.excluded_items : [];
  // The one reason fixed on Supplier setup: it earns that CTA, and the
  // permanent opt-out, which mutes the missing_supplier issue specifically.
  const missingSupplierExcluded = exclusions.some(({ reason }) => reason === 'missing_supplier');
  // Every reason has a Data health card (the backend's reason → issue map,
  // pinned by test_planning_summary_excluded_items.py), so any exclusion
  // on the banner earns that CTA.
  const healthGap = exclusions.length > 0;
  // Plural-aware (has/have), hence the count.
  const exclusionPhrase = (reason, count) =>
    t(`purchaseRecommendations.itemsExcluded.reasons.${reason}`, { count });

  // The last FINISHED run failed: the pending recommendations are an older
  // run's.
  const runFailed = planningSummary?.last_run_failed;
  const showEmergencyEarly = emergencyAlertsEnabled && emergencyEarly > 0;

  // The banner's rows in fix-first order, keyed by condition; a falsy row is a
  // condition that does not hold.
  const attentionRows = {
    runFailed: runFailed && t('purchaseRecommendations.lastCalculationFailed'),
    // One reason reads as a sentence ("28 items are not included because
    // they have no default supplier"); several list their counts. Reasons
    // arrive in fix-first order (the engine's gate order), so the first one
    // listed is the one to fix first.
    excludedItems: healthGap && (exclusions.length === 1
      ? t('purchaseRecommendations.itemsExcluded.single', {
        count: exclusions[0].count,
        reason: exclusionPhrase(exclusions[0].reason, exclusions[0].count),
      })
      : t('purchaseRecommendations.itemsExcluded.summary', {
        reasons: exclusions
          .map(({ reason, count }) => t('purchaseRecommendations.itemsExcluded.reason', {
            count, reason: exclusionPhrase(reason, count),
          }))
          .join(', '),
      })),
    recommendationsBlocked: recommendationsBlocked > 0
      && t('purchaseRecommendations.recommendationsBlocked', { count: recommendationsBlocked }),
    recommendationsStale: recommendationsStale > 0
      && t('purchaseRecommendations.recommendationsStale', { count: recommendationsStale }),
    emergencyEarly: showEmergencyEarly
      && t('purchaseRecommendations.emergencyEarly', { count: emergencyEarly }),
  };
  // The conditions on screen; each exclusion reason is its own, so a reason
  // that newly holds items out is new news even beside another one.
  const conditionsSignature = Object.keys(attentionRows)
    .filter((key) => attentionRows[key])
    .flatMap((key) => (key === 'excludedItems'
      ? exclusions.map(({ reason }) => `excludedItems:${reason}`)
      : [key]))
    .join(' ');
  const liveConditions = useMemo(
    () => (conditionsSignature ? conditionsSignature.split(' ') : []),
    [conditionsSignature],
  );

  // The X hides the conditions on screen when it was pressed. A condition that
  // clears leaves that list, so one that comes back — an alert switched on
  // again in Store settings, a new failure — brings the banner back with it.
  const [dismissedConditions, setDismissedConditions] = useState([]);
  useEffect(() => {
    setDismissedConditions((dismissed) => (
      dismissed.every((key) => liveConditions.includes(key))
        ? dismissed
        : dismissed.filter((key) => liveConditions.includes(key))));
  }, [liveConditions]);
  const attentionDismissed = liveConditions.length > 0
    && liveConditions.every((key) => dismissedConditions.includes(key));

  // The X. With no opt-out row on screen it just hides the banner. Otherwise
  // it asks once, one paragraph per opt-out present, and
  // confirming persists all of them: the missing_supplier Data Health mute
  // (Store.ignored_health_issues, re-enableable from Data health — which also
  // drops the reason from this banner, as planning-summary leaves muted issues
  // out) and/or the early-alerts planning preference (re-enableable under
  // Settings → Planning). Cancelling the dialog, or clicking outside it, keeps
  // the banner. A failed persist brings the banner back so the merchant can
  // retry rather than silently losing a warning.
  const handleDismissAttention = useCallback(async () => {
    const optOuts = [
      missingSupplierExcluded && 'missingSupplier',
      showEmergencyEarly && 'emergencyEarly',
    ].filter(Boolean);
    if (optOuts.length === 0) {
      setDismissedConditions(liveConditions);
      return;
    }
    const turnOff = await confirm({
      variant: 'warning',
      title: t('purchaseRecommendations.needsAttention.turnOffDialog.title', { count: optOuts.length }),
      message: optOuts
        .map((key) => t(`purchaseRecommendations.needsAttention.turnOffDialog.items.${key}`))
        .join('\n\n'),
      confirmLabel: t('purchaseRecommendations.needsAttention.turnOffDialog.confirm', { count: optOuts.length }),
    });
    if (!turnOff) return;
    setDismissedConditions(liveConditions);
    try {
      if (optOuts.includes('missingSupplier')) {
        await setIssueIgnored.mutateAsync({ issue: 'missing_supplier', ignored: true });
      }
      if (optOuts.includes('emergencyEarly')) {
        await sessionService.updateStoreSettings({
          planning: { emergency_alerts_before_order_day: false },
        });
        changed([DATA.settings]);
      }
    } catch (err) {
      console.error('Failed to turn off attention warnings', err);
      setDismissedConditions([]);
      showToast(translateRequestError(err, t, 'purchaseRecommendations.needsAttention.turnOffDialog.error'), 'error');
    }
  }, [confirm, t, missingSupplierExcluded, showEmergencyEarly, liveConditions, setIssueIgnored, changed]);

  // Poll for running job completion. Used by both on-mount check and recalculate click.
  const pollCancelledRef = useRef(false);
  const pollTimerRef = useRef(null);
  // Each tick runs the CURRENT render's poll. The mount check arms the first
  // timer once, from its first render; a poll that re-armed itself would keep
  // that render's closure for the whole run.
  const pollRef = useRef(null);
  const armPoll = useCallback(() => {
    pollTimerRef.current = setTimeout(() => pollRef.current(), 3000);
  }, []);

  // The completed-job id the grid's current rows were fetched under.
  // Recommendation ids are valid only within one calculation run — every run
  // (manual, nightly DAG, another session) deletes all pending recommendations
  // and recreates them with new ids (purchase_orders/services.py
  // _replace_pending_recommendations) — so this id changing means every row
  // and cached detail on screen is dead. undefined = not yet established
  // (initial check pending/failed, or a run in flight); null = established:
  // no calculation has ever completed for this store.
  const rowsJobIdRef = useRef(undefined);

  // A completed run replaces the recommendations every page reads — the home
  // tables, the planning banner, the KPIs, the bell badge, the stockout
  // report — so it is announced as a new recommendation run
  // (`queries/dataChanged`), whichever detector saw it (the completion poll,
  // the focus-time re-check, or a dead-id 404).
  const pollForCompletion = useCallback(async () => {
    try {
      const res = await sessionApi.get(LATEST_JOB_URL);
      if (pollCancelledRef.current) return;
      // An error answer ends the run's spinner the way a network failure does.
      if (!res?.ok) throw new Error(`HTTP ${res?.status}`);
      const data = await res.json();
      const latest = data.results?.[0];
      if (latest?.Status === 'running') {
        armPoll();
      } else {
        rowsJobIdRef.current = latest?.POCalculationJobID ?? null;
        // Awaited: the spinner ends once the planning summary has been read
        // again; until then the cache describes the recommendations the run
        // replaced.
        await changed([DATA.recommendationRun]);
        if (pollCancelledRef.current) return;
        // Re-arm dismissal so what a brand-new run left needing attention (a
        // new failure, recommendations to review) shows again even if the
        // user had dismissed the prior run's banner. A failed run needs no
        // toast: the re-armed banner says so, and the grid's no-rows copy
        // takes over once the banner is dismissed (noRowsTitle).
        setDismissedConditions([]);
        setRunningCalculation(false);
      }
    } catch (err) {
      console.error('PO calculation poll failed:', err);
      if (!pollCancelledRef.current) {
        setRunningCalculation(false);
        showToast(t('purchaseRecommendations.messages.statusCheckFailed'), 'error');
      }
    }
  }, [armPoll, changed, t]);
  useEffect(() => { pollRef.current = pollForCompletion; }, [pollForCompletion]);

  // On mount: check if a calculation is already running (e.g. user navigated away and back).
  useEffect(() => {
    pollCancelledRef.current = false;

    const checkRunning = async () => {
      try {
        const res = await sessionApi.get(LATEST_JOB_URL);
        if (pollCancelledRef.current) { setInitialCheckDone(true); return; }
        if (!res?.ok) throw new Error(`HTTP ${res?.status}`);
        const data = await res.json();
        const latest = data.results?.[0];
        if (latest?.Status === 'running') {
          setRunningCalculation(true);
          armPoll();
        } else {
          // The rows about to load belong to this run — the baseline the
          // focus-time staleness check compares against. A running job's id
          // is NOT recorded: pollForCompletion stamps it on completion.
          rowsJobIdRef.current = latest?.POCalculationJobID ?? null;
        }
      } catch (err) {
        console.error('PO initial check failed:', err);
        showToast(t('purchaseRecommendations.messages.statusCheckFailed'), 'error');
      }
      if (!pollCancelledRef.current) setInitialCheckDone(true);
    };

    checkRunning();
    return () => { pollCancelledRef.current = true; clearTimeout(pollTimerRef.current); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const gridApiRef = useRef(null);
  const expandAll = useExpandAllRows();
  // The lines the merchant unticked: Map<recId, Map<lineId, line>>. Each keeps
  // its quantity and cost, so the row's totals leave it out (tickedRow) over
  // whatever totals a reload of the row brings.
  const excludedLinesRef = useRef(new Map());
  const pageSize = 100;

  // The row's totals over the lines left ticked. The pinned totals row has no
  // recommendation id, so it keeps its own.
  const tickedRow = useCallback(
    (data) => tickedTotals(data, excludedLinesRef.current.get(data?.PORecommendationID)),
    [],
  );

  // A dead recommendation id (404 on detail expand or on the Create-PO
  // prefill) means a calculation run replaced every pending recommendation
  // since these rows were fetched — the same event the focus-time check
  // detects, reached through a different door. One purge + one informational
  // toast per superseded run, however many expanded details 404 at once;
  // re-armed when fresh rows land (handleSsrmData).
  const supersededHandledRef = useRef(false);

  const handleRecommendationsSuperseded = useCallback(() => {
    if (supersededHandledRef.current) return;
    supersededHandledRef.current = true;
    // The 404 can arrive while the replacing run is still in flight — before
    // any new completed job exists — so the rows' run id is unknown here.
    // Reset to "not established" and let the next jobs check adopt the new id
    // rather than purging a second time.
    rowsJobIdRef.current = undefined;
    showToast(t('purchaseRecommendations.messages.recalculated'), 'info');
    changed([DATA.recommendationRun]);
  }, [t, changed]);

  // Include/exclude wiring handed to the detail sub-grids (RecoDetail) via
  // detailCellRendererParams. It lives here — not in the detail — because the
  // master row's totals read it (tickedRow) and the unticked lines outlive any
  // one expanded detail (the Create-PO flow reads them).
  const recordUnticked = useCallback((recId, unticked) => {
    if (unticked.size > 0) excludedLinesRef.current.set(recId, unticked);
    else excludedLinesRef.current.delete(recId);
    const masterNode = gridApiRef.current?.getRowNode(String(recId));
    if (masterNode) gridApiRef.current.refreshCells({ rowNodes: [masterNode], force: true });
  }, []);
  const recoDetailContext = useMemo(() => ({
    excludedLineIds: (recId) => untickedLineIds(excludedLinesRef.current, recId),
    setLineSelection: (recId, detailApi) => {
      const unticked = new Map();
      detailApi.forEachNode((node) => {
        if (!node.isSelected()) unticked.set(node.data.PORecommendationLineID, node.data);
      });
      recordUnticked(recId, unticked);
    },
    onRecommendationsSuperseded: handleRecommendationsSuperseded,
  }), [recordUnticked, handleRecommendationsSuperseded]);

  // A change the grid reloads its rows for can move what an unticked line is
  // worth (a new cost) or remove it, while a collapsed detail reads nothing.
  // The page reads those lines again, so the row's totals take off what the
  // server holds now. A new run killed every id: nothing is left to take off.
  useOnDataChanged(gridReads(RECOMMENDATIONS_URL).reads, (kinds) => {
    if (kinds.includes(DATA.recommendationRun)) {
      excludedLinesRef.current.clear();
      return;
    }
    excludedLinesRef.current.forEach((unticked, recId) => {
      queryClient.fetchQuery(recommendationLinesQuery(selectedStore?.id, recId))
        .then((lines) => recordUnticked(recId, new Map(lines
          .filter((line) => unticked.has(line.PORecommendationLineID))
          .map((line) => [line.PORecommendationLineID, line]))))
        .catch((err) => {
          // The recommendation was deleted (with its supplier, or with its
          // last line): the grid's reload drops its row.
          if (err?.status === 404) recordUnticked(recId, new Map());
          else showToast(translateRequestError(err, t, 'purchaseRecommendations.messages.loadFailed'), 'error');
        });
    });
  });

  const detailCellRendererParams = useMemo(() => ({ context: recoDetailContext }), [recoDetailContext]);

  const columnDefs = useMemo(() => wrapColumnsForSummaryRows([
    {
      field: "supplier_name",
      headerName: t('purchaseRecommendations.columns.supplier'),
      // Supplier and Location are the only text columns, so they share the
      // width the measured columns leave over; the supplier name gets the
      // larger share.
      ...flexColumn(2),
      cellRenderer: 'agGroupCellRenderer',
      // valueFormatter drives clipboard/quick-filter text; the innerRenderer
      // owns the on-screen cell (agGroupCellRenderer applies no formatter to it)
      // and adds the "Supplier changed" badge when the rec's batched supplier no
      // longer matches a line item's current default (backend is_stale).
      valueFormatter: (params) => params.value || t('purchaseRecommendations.noSupplier'),
      cellRendererParams: {
        innerRenderer: (params) => {
          // The totals row's label lives in this column: it is printed as is,
          // never as "No supplier" or with a stale badge.
          if (isPinnedTotalRow(params)) return params.value;
          const name = params.value || t('purchaseRecommendations.noSupplier');
          if (!params.data.is_stale) return name;
          // A "Supplier changed" chip would crowd out the name it qualifies.
          // Use the same warning triangle we use elsewhere, with the full
          // message on hover, so the cue is legible at any column width.
          return (
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
              {name}
              <Tooltip title={t('purchaseRecommendations.supplierChangedBadge')}>
                <Box
                  component="span"
                  role="img"
                  aria-label={t('purchaseRecommendations.supplierChangedBadge')}
                  sx={{ display: 'inline-flex', color: 'warning.main' }}
                >
                  <Warning size={ICON.md} weight="fill" />
                </Box>
              </Tooltip>
            </Box>
          );
        },
      },
    },
    {
      field: "location_name",
      headerName: t('purchaseRecommendations.columns.location'),
      ...flexColumn(),
      valueFormatter: (params) => params.value || '-',
    },
    {
      field: "RecommendedOrderDate",
      headerName: t('purchaseRecommendations.columns.nextOrderDate'),
      // PORecommendationViewSet.ordering: the order the rows come in when
      // the request names none.
      initialSort: 'asc',
      type: 'dateColumn',
      valueFormatter: (params) => {
        if (!params.value) return '';
        return formatDate(params.value);
      },
      cellClassRules: {
        // Past the store's own day, not the viewer's. The two disagree for
        // hours around midnight, and this cell is what tells a merchant they
        // are late to order.
        'ag-cell-attention-error': (params) => {
          if (!params.value || !storeToday) return false;
          return params.value < storeToday;
        },
      }
    },
    {
      // The rec's shortage, in the Home Stockout risk grid's two red words
      // (PORecommendationLine.shortage): an emergency line — the engine's
      // stock position, shelf plus inbound landing within the lead time, at
      // or below the lead-time demand, or the safety stock under Safety stock
      // mode — whatever the rec's order date and whether or not the store
      // turned early alerts off. That setting decides only where the rec is
      // flagged (banner, bell, Due today), never what its stock is. The lines
      // grid marks the line itself (RunsOutCellRenderer). Not sortable: no
      // server ordering matches this rule, and the grid sorts server-side.
      colId: 'status',
      headerName: t('purchaseRecommendations.columns.status'),
      headerTooltip: t('purchaseRecommendations.columns.statusTooltip'),
      type: 'centeredColumn',
      sortable: false,
      valueGetter: (params) => {
        if (isPinnedTotalRow(params)) return null;
        return params.data.shortage === null ? 'onSchedule' : params.data.shortage;
      },
      cellRenderer: (params) => {
        if (params.value === null) return null;
        const chips = {
          onSchedule: <StatusChip label={t('purchaseRecommendations.columns.statusOnSchedule')} color="success" />,
          out_of_stock: <StatusChip label={t('purchaseRecommendations.detail.runsOutAlready')} color="error" />,
          will_run_out: <StatusChip label={t('purchaseRecommendations.columns.statusWillRunOut')} color="error" />,
        };
        if (!(params.value in chips)) throw new Error(`Status: unknown shortage ${params.value}`);
        const { [params.value]: chip, ...others } = chips;
        // "Out of stock" explains itself; the other red word gets the fact.
        const shown = params.value === 'will_run_out'
          ? <Tooltip title={t('purchaseRecommendations.columns.statusWillRunOutTooltip')}>{chip}</Tooltip>
          : chip;
        return <ChipSlot shown={shown} alternatives={Object.values(others)} />;
      },
    },
    {
      // Read-only: the PO's delivery date is set in the PO form.
      field: "ExpectedDeliveryDate",
      headerName: t('purchaseRecommendations.columns.expectedDeliveryDate'),
      type: 'dateColumn',
      editable: false,
      valueFormatter: (params) => {
        if (!params.value) return '';
        return formatDate(params.value);
      },
    },
    {
      field: "total_quantity",
      headerName: t('purchaseRecommendations.columns.totalQty'),
      type: 'numberColumn',
      valueGetter: (params) => tickedRow(params.data)?.total_quantity,
      valueFormatter: (params) => {
        if (params.value === null || params.value === undefined) return '0';
        return formatNumber(params.value, 0);
      }
    },
    {
      // Per-cell supplier-ccy formatter. Each rec is supplier-scoped → one
      // currency per row, so the column header stays currency-free ("Est.
      // Total (excl. tax)") and each cell renders its row's supplier
      // currency. The pinned totals row carries no supplier_currency_code
      // (backend `totals`), so it renders a plain number.
      //
      // Server-side sort: the displayed value is per-row supplier ccy,
      // but the comparison key is the store-ccy aggregate annotated
      // by PORecommendationViewSet._annotate_total_value_reporting_ccy.
      // That keeps "biggest order first" meaningful across mixed-currency
      // result sets, which the supplier-ccy raw number cannot.
      field: "total_value",
      headerName: t('purchaseRecommendations.columns.totalValueEst'),
      headerTooltip: t('purchaseRecommendations.columns.totalValueEstTooltip'),
      type: 'numberColumn',
      valueGetter: (params) => tickedRow(params.data)?.total_value,
      // An estimate the backend leaves NULL when no line on the rec can be
      // priced, and short by `uncosted_lines` lines otherwise (items with no
      // cost of record — the same lines a PO created from it saves unpriced).
      // A '0' here would be a price nobody set, on the figure a salesperson
      // sizes the order by.
      valueFormatter: (params) => {
        if (params.value === null || params.value === undefined) return '—';
        const ccy = params.data.supplier_currency_code;
        return ccy
          ? formatCurrency(params.value, ccy)
          : formatNumber(params.value, 2);
      },
      cellRenderer: (params) => {
        const text = params.valueFormatted ?? '';
        // The pinned totals row carries its own `uncosted_lines` and
        // `fx_unavailable` (backend `totals`), so this reads the same way for
        // a row and for the total.
        const warning = totalValueWarning(tickedRow(params.data), t);
        if (!warning) return text;
        return (
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            {text}
            <Tooltip title={warning}>
              <Box
                component="span"
                role="img"
                aria-label={warning}
                sx={{ display: 'inline-flex', color: 'warning.main' }}
              >
                <Warning size={ICON.md} weight="fill" />
              </Box>
            </Tooltip>
          </Box>
        );
      },
    },
    actionColumn({
      headerName: t('purchaseRecommendations.columns.actions'),
      cellRenderer: (params) => {
        if (isPinnedTotalRow(params)) return null;
        return (
          <RecommendationRowActions
            recommendation={params.data}
            onCreatePO={handleCreatePO}
            onLinkSupplier={handleLinkSupplier}
          />
        );
      }
    })
  ]), [t, formatNumber, formatDate, formatCurrency, storeToday, tickedRow]);

  // Pinned-bottom totals row — sums total_quantity (recommended units across
  // the filtered set). Label sits in supplier_name column (first text column).
  const pinnedBottomRowData = useMemo(
    () => buildPinnedTotalsRow(totals, 'supplier_name', t('grid.totals.label')),
    [totals, t],
  );

  // Every filter but the "Due today" / "All" buttons' — the scope they count in.
  // The merchant's own filters (the filter bar); the page adds BASE_SCOPE.
  const userFilters = useMemo(() => ({
    ...itemFilter.params,
    product_category_id: selectedProductCategory?.id || null,
    supplier_id: selectedSupplier?.supplier_id ?? null,
    location_code: selectedLocation === 'all' ? null : selectedLocation,
  }), [itemFilter.params, selectedProductCategory, selectedSupplier, selectedLocation]);
  // An empty grid under any of them means "no matches", not "nothing to order".
  // An unset filter is null here and absent from the item params.
  const filtersActive = Object.values(userFilters).some((value) => value !== null);
  const otherFilters = useMemo(() => ({ ...BASE_SCOPE, ...userFilters }), [userFilters]);
  const ssrmParams = useMemo(
    () => ({ ...otherFilters, actionable_today: filterActionable ? 'true' : null }),
    [otherFilters, filterActionable],
  );
  // Counts of the previous scope would sit over the reloading grid.
  useChangeEffect(otherFilters, () => setFilterCounts(null));

  const exportProps = {
    endpoint: '/api/po-calculations/recommendations/export/',
    filename: 'po_recommendations',
    // 'filtered' exports the rows the grid shows; 'all' every pending rec.
    getParams: (scope) => buildExportQuery(scope === 'all' ? BASE_SCOPE : ssrmParams),
  };

  const handleSsrmData = useCallback((data) => {
    // Fresh rows: the next superseded run is a new event, toast it again.
    supersededHandledRef.current = false;
    setTotals(data.totals);
    setFilterCounts(data.filter_counts);
  }, []);

  // Single home for the no-rows copy, given to the grid's <GridEmptyState>.
  // undefined falls through to NoRowsOverlay's generic copy.
  // The overlay shows only on an empty grid, so under "Due today" a non-zero
  // "All" count means pending recs exist outside today under the same filters.
  const noRowsTitle = useMemo(() => {
    // An empty grid after a crashed run is not "nothing to reorder". While the
    // attention banner states the failure the overlay stays generic, so the
    // sentence shows once; after a dismissal the overlay carries it.
    if (runFailed) {
      return attentionDismissed ? t('purchaseRecommendations.lastCalculationFailed') : undefined;
    }
    if (!filterCounts) return undefined;
    return filterActionable && filterCounts.all > 0
      ? t('purchaseRecommendations.noOrdersDueToday', { all: t('purchaseRecommendations.allFilter') })
      : t('purchaseRecommendations.allInventoryHealthy');
  }, [runFailed, attentionDismissed, filterCounts, filterActionable, t]);

  const handleSsrmError = useCallback((err) => {
    showToast(translateRequestError(err, t, 'purchaseRecommendations.messages.loadFailed'), 'error');
  }, [t]);

  // Stable identity: getRowId is an AG Grid initial property, so a new
  // function on a later render cannot be applied (warning #22).
  const getRowId = useCallback((params) => String(params.data.PORecommendationID), []);

  const { onGridReady, onGridPreDestroyed, pagination } = useSsrmGrid({
    endpoint: RECOMMENDATIONS_URL,
    params: ssrmParams,
    pageSize,
    onData: handleSsrmData,
    onError: handleSsrmError,
    gridRef: gridApiRef,
  });

  // ── Staleness check when the page comes back into view ──
  // A page left open while the nightly DAG (core/scheduling) or another
  // session recalculated: overnight in a visible window, or in a tab the
  // merchant switched away from (every open tab stays mounted, with its
  // grid). Each run replaces all pending recommendations with new ids, so on
  // regaining the window's focus or visibility, and when the tab comes back
  // on screen, we re-check the latest job; a different completed id purges
  // the grid and refreshes the caches that mirror the queue. These three
  // events are the whole trigger set — no continuous polling. A tab off
  // screen sits out the window's events: its own return is its check, so a
  // focus does not reload a grid nobody is looking at.
  const tabActive = useTabActive();
  const staleCheckInFlightRef = useRef(false);
  const recheckLatestJob = useCallback(async () => {
    // visibilitychange also fires on hide; a run started from this page
    // already owns the grid via pollForCompletion.
    if (document.visibilityState === 'hidden' || !tabActive) return;
    if (runningCalculation || staleCheckInFlightRef.current) return;
    staleCheckInFlightRef.current = true;
    try {
      const res = await sessionApi.get(LATEST_JOB_URL);
      if (!res?.ok) {
        // Probe only — the grid keeps what it has, the stale-submit 400
        // backstop still guards the write path, and the next regain retries.
        console.error('PO recommendations staleness check failed: HTTP', res?.status);
        return;
      }
      const latest = (await res.json()).results?.[0];
      // A run in flight has no stable id to adopt yet; its completion is
      // caught on the next regain (or by this page's own poll if it
      // started the run).
      if (!latest || latest.Status === 'running') return;
      const jobId = latest.POCalculationJobID;
      if (rowsJobIdRef.current === undefined) {
        // The rows' own run was never established (initial check failed,
        // or a 404 purge reset it) — adopt this id rather than purging on
        // a guess.
        rowsJobIdRef.current = jobId;
        return;
      }
      if (rowsJobIdRef.current === jobId) return;
      rowsJobIdRef.current = jobId;
      changed([DATA.recommendationRun]);
    } catch (err) {
      console.error('PO recommendations staleness check failed:', err?.message);
    } finally {
      staleCheckInFlightRef.current = false;
    }
  }, [tabActive, runningCalculation, changed]);
  useEffect(() => {
    window.addEventListener('focus', recheckLatestJob);
    document.addEventListener('visibilitychange', recheckLatestJob);
    return () => {
      window.removeEventListener('focus', recheckLatestJob);
      document.removeEventListener('visibilitychange', recheckLatestJob);
    };
  }, [recheckLatestJob]);
  useChangeEffect(tabActive, () => {
    if (tabActive) recheckLatestJob();
  });

  const handleRunCalculation = async () => {
    if (recalcInFlightRef.current) return;
    recalcInFlightRef.current = true;

    try {
      const data = await runJobMutation.mutateAsync();

      if (data?.status === 'up_to_date') {
        showToast(t('purchaseRecommendations.messages.calculationCached'), "success");
        return;
      }

      // "started" or "running" — show spinner and poll for completion
      setRunningCalculation(true);
      pollCancelledRef.current = false;
      armPoll();
    } catch (error) {
      console.error('purchaseRecommendations recalc:', error.message);
      showToast(translateRequestError(error, t, 'purchaseRecommendations.messages.calculationFailed'), "error");
    } finally {
      recalcInFlightRef.current = false;
    }
  };

  // "Review & create" opens the unified PurchaseOrderForm pre-filled from the
  // recommendation. The form is the single authoring surface.
  const [poFormOpen, setPoFormOpen] = useState(false);
  const [poFormRecId, setPoFormRecId] = useState(null);
  const [poFormExcluded, setPoFormExcluded] = useState([]);

  const handleCreatePO = (recommendation) => {
    const recId = recommendation.PORecommendationID;
    const excludedIds = [...untickedLineIds(excludedLinesRef.current, recId)];
    setPoFormRecId(recId);
    setPoFormExcluded(excludedIds);
    setPoFormOpen(true);
  };

  // A manual PO is the same authoring surface with nothing pre-filled — the
  // merchant picks the supplier and lines instead of the engine proposing them.
  const handleCreateManualPO = () => {
    setPoFormRecId(null);
    setPoFormExcluded([]);
    setPoFormOpen(true);
  };

  const handleLinkSupplier = (recommendation) => {
    setLinkRec(recommendation);
  };

  // The link write names the suppliers as changed: the grid rows and the
  // banner read again by themselves.
  const handleSupplierLinked = useCallback(() => setLinkRec(null), []);

  // The rec died between "Review & create" and the prefill (its run was replaced).
  // Close the form the merchant can no longer submit and route into the same
  // purge + toast the detail-expand 404 takes.
  const handleRecommendationGone = useCallback(() => {
    setPoFormOpen(false);
    setPoFormRecId(null);
    handleRecommendationsSuperseded();
  }, [handleRecommendationsSuperseded]);

  // Creating a PO from a recommendation consumes the recommendation; the
  // change every create announces (usePurchaseOrderCreated) reloads the rows
  // in place.
  const handlePoFormSaved = useCallback((saved) => {
    if (poFormRecId != null) {
      excludedLinesRef.current.delete(poFormRecId);
    }
    onPurchaseOrderCreated(saved);
  }, [poFormRecId, onPurchaseOrderCreated]);

  return (
    <div className="page-container">
      <div className="tiles-row">
        <Tile
          width="full"
          title={t('purchaseRecommendations.tileTitle')}
          subtitle={t('purchaseRecommendations.subtitle')}
          subtitleAutoHide
          toolbar={
            <>
              <FilterChips
                options={filterOptions}
                value={filterActionable ? 'dueToday' : 'all'}
                onChange={pressFilter}
                disabled={runningCalculation}
              />
              {canCreatePO && (
                <Button variant="contained" startIcon={<Plus size={ICON.md} />} onClick={handleCreateManualPO}>
                  {t('purchaseOrders.create.button')}
                </Button>
              )}
            </>
          }
          headerActions={
            <>
              <Button
                variant="outlined"
                startIcon={<ArrowsClockwise size={ICON.md} />}
                onClick={handleRunCalculation}
                disabled={runningCalculation}
              >
                {t('purchaseRecommendations.actions.recalculate')}
              </Button>
              <Button variant="outlined" onClick={openReorderLevels}>
                {t('reorderLevels.title')}
              </Button>
              <GridExportButton {...exportProps} label={t('purchaseRecommendations.actions.export')} />
            </>
          }
        >
          <GridPage>
            {runningCalculation && (
              <div className="grid-page-loading">
                <CircularProgress size={40} />
                <Typography variant="h6" className="grid-page-loading-text">
                  {t('purchaseRecommendations.actions.generating')}
                </Typography>
              </div>
            )}

            {!runningCalculation && initialCheckDone && (
              <>
                <PageBanner severity="error" messages={[planningSummaryError]} />
                <PageBanner severity="error" messages={[connectorsError]} />
                {/*
                  * One banner for every condition that needs the merchant's
                  * attention, in fix-first order. The X hides all of it until a
                  * condition it did not hide appears (and offers the permanent
                  * opt-outs, see handleDismissAttention); a failed run keeps explaining itself in the grid's
                  * no-rows overlay (noRowsTitle above), so dismissing the
                  * banner doesn't leave an empty grid unexplained. Severity follows the worst row: a crashed run is
                  * an error, everything else is a warning.
                  */}
                {!attentionDismissed && (
                  <PageBanner
                    severity={runFailed ? 'error' : 'warning'}
                    onDismiss={handleDismissAttention}
                    actions={[
                      healthGap && {
                        label: t('purchaseRecommendations.itemsExcluded.action'),
                        onClick: () => openTab('/data-health'),
                      },
                      (missingSupplierExcluded || recommendationsBlocked > 0) && {
                        label: t('purchaseRecommendations.needsAttention.supplierSetupAction'),
                        onClick: () => openTab('/supplier-setup'),
                      },
                    ]}
                    messages={Object.values(attentionRows)}
                  />
                )}
                <ItemFilterBar
                  filter={itemFilter}
                  productCategoryFilter={{
                    selected: selectedProductCategory,
                    set: setSelectedProductCategory,
                    show: true,
                  }}
                  supplierFilter={{
                    selected: selectedSupplier,
                    set: setSelectedSupplier,
                  }}
                  locationFilter={{
                    selected: selectedLocation,
                    set: setSelectedLocation,
                  }}
                />
                <ExpandAllButton {...expandAll} />
                <GridViewport>
                  {gridTheme && (
                    <GridEmptyState
                      title={noRowsTitle}
                      filtersActive={filtersActive}
                    >
                      <AgGridReact
                        {...defaultGridProps}
                        {...editableGridProps}
                        theme={gridTheme}
                        {...gridState}
                        columnDefs={columnDefs}
                        getRowId={getRowId}
                        defaultColDef={defaultColDef}
                        columnTypes={columnTypes}
                        onGridReady={onGridReady}
                        onGridPreDestroyed={onGridPreDestroyed}
                        rowModelType="serverSide"
                        serverSideSortAllLevels={true}
                        {...pagination}
                        masterDetail={true}
                        detailCellRenderer={RecoDetail}
                        detailCellRendererParams={detailCellRendererParams}
                        detailRowAutoHeight={true}
                        isRowMaster={everyRowIsMaster}
                        {...expandAll.gridProps}
                        pinnedBottomRowData={pinnedBottomRowData}
                        getContextMenuItems={copyOnlyContextMenuItems}
                      />
                    </GridEmptyState>
                  )}
                </GridViewport>
              </>
            )}
          </GridPage>
        </Tile>
      </div>

      <ConfirmDialog {...dialogProps} />
      {/* Mounted only while open: the form carries its own AG Grid, and the
          draft hook resets and loads on `open` anyway, so keeping it mounted
          would buy a second grid per page and nothing else. */}
      {poFormOpen && (
        <PurchaseOrderForm
          open
          onClose={() => setPoFormOpen(false)}
          onSaved={handlePoFormSaved}
          recommendationId={poFormRecId}
          excludedLineIds={poFormExcluded}
          onRecommendationGone={handleRecommendationGone}
        />
      )}
      {erpConnector && linkRec && (
        <LinkSupplierToExactDialog
          connectorId={erpConnector.id}
          supplier={{ supplier_id: linkRec.supplier_id, supplier_name: linkRec.supplier_name }}
          open={!!linkRec}
          onClose={() => setLinkRec(null)}
          onLinked={handleSupplierLinked}
        />
      )}
    </div>
  );
}

export default PurchaseRecommendations;
